import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Pressable,
  PanResponder,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  useWindowDimensions
} from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'

export interface CustomTimePickerModalProps {
  visible: boolean
  value?: Date | number
  date?: Date | number
  onConfirm: (selectedDate: Date) => void
  onCancel: () => void
  testID?: string
}

const DIAL_SIZE = 248
const RADIUS = DIAL_SIZE / 2
const NUMBER_RADIUS = 88
const ITEM_SIZE = 38
const PRIMARY_TEAL = '#0B495C'
const CONFIRM_GREEN = '#9DD086'
const CANCEL_RED = '#D81616'

export const CustomTimePickerModal: React.FC<CustomTimePickerModalProps> = ({
  visible,
  value,
  date,
  onConfirm,
  onCancel
}) => {
  const { width: DEVICE_WIDTH } = useWindowDimensions()

  const getInitialDate = (): Date => {
    const d = value ?? date
    if (d instanceof Date) return d
    if (typeof d === 'number') return new Date(d)
    return new Date()
  }

  const [selectedHour, setSelectedHour] = useState<number>(5)
  const [selectedMinute, setSelectedMinute] = useState<number>(0)
  const [period, setPeriod] = useState<'AM' | 'PM'>('PM')
  const [mode, setMode] = useState<'hour' | 'minute'>('hour')
  const [isKeyboardMode, setIsKeyboardMode] = useState<boolean>(false)

  // Keyboard text inputs
  const [kbHour, setKbHour] = useState<string>('5')
  const [kbMinute, setKbMinute] = useState<string>('00')

  useEffect(() => {
    if (visible) {
      const initial = getInitialDate()
      let hours = initial.getHours()
      const minutes = initial.getMinutes()
      const isPm = hours >= 12
      hours = hours % 12
      if (hours === 0) hours = 12

      setSelectedHour(hours)
      setSelectedMinute(minutes)
      setPeriod(isPm ? 'PM' : 'AM')
      setMode('hour')
      setIsKeyboardMode(false)
      setKbHour(`${hours}`)
      setKbMinute(minutes < 10 ? `0${minutes}` : `${minutes}`)
    }
  }, [visible, value, date])

  const calculateFromTouch = (
    touchX: number,
    touchY: number,
    currentMode: 'hour' | 'minute'
  ) => {
    const rx = touchX - RADIUS
    const ry = touchY - RADIUS
    let angleRad = Math.atan2(rx, -ry)
    if (angleRad < 0) {
      angleRad += 2 * Math.PI
    }
    const angleDeg = (angleRad * 180) / Math.PI

    if (currentMode === 'hour') {
      let h = Math.round(angleDeg / 30) % 12
      if (h === 0) h = 12
      setSelectedHour(h)
      setKbHour(`${h}`)
    } else {
      let m = Math.round(angleDeg / 6) % 60
      setSelectedMinute(m)
      setKbMinute(m < 10 ? `0${m}` : `${m}`)
    }
  }

  const modeRef = useRef(mode)
  useEffect(() => {
    modeRef.current = mode
  }, [mode])

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: (evt) => {
        const { locationX, locationY } = evt.nativeEvent
        calculateFromTouch(locationX, locationY, modeRef.current)
      },
      onPanResponderMove: (evt) => {
        const { locationX, locationY } = evt.nativeEvent
        calculateFromTouch(locationX, locationY, modeRef.current)
      },
      onPanResponderRelease: () => {
        if (modeRef.current === 'hour') {
          setTimeout(() => {
            setMode('minute')
          }, 200)
        }
      }
    })
  ).current

  const handleConfirm = () => {
    const finalDate = getInitialDate()
    let h = isKeyboardMode ? parseInt(kbHour, 10) || 12 : selectedHour
    let m = isKeyboardMode ? parseInt(kbMinute, 10) || 0 : selectedMinute

    if (h < 1) h = 1
    if (h > 12) h = 12
    if (m < 0) m = 0
    if (m > 59) m = 59

    let hours24 = h % 12
    if (period === 'PM') {
      hours24 += 12
    }

    finalDate.setHours(hours24, m, 0, 0)
    onConfirm(finalDate)
  }

  const selectHourDirect = (h: number) => {
    setSelectedHour(h)
    setKbHour(`${h}`)
    setTimeout(() => {
      setMode('minute')
    }, 200)
  }

  const selectMinuteDirect = (m: number) => {
    setSelectedMinute(m)
    setKbMinute(m < 10 ? `0${m}` : `${m}`)
  }

  // Calculate hand line and selected indicator positions
  const currentAngleDeg =
    mode === 'hour' ? (selectedHour % 12) * 30 : selectedMinute * 6

  const currentAngleRad = (currentAngleDeg * Math.PI) / 180
  const targetX = RADIUS + NUMBER_RADIUS * Math.sin(currentAngleRad)
  const targetY = RADIUS - NUMBER_RADIUS * Math.cos(currentAngleRad)

  const midX = (RADIUS + targetX) / 2
  const midY = (RADIUS + targetY) / 2

  const hoursList = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
  const minutesList = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]

  if (!visible) return null

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <SafeAreaView style={{ flexGrow: 1 }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={[{ flex: 1 }, DEVICE_WIDTH > 960 && styles.modalLargeScreen]}
        >
          <View style={styles.overlay}>
            <Pressable style={styles.backdrop} onPress={onCancel} />
            <View style={styles.card}>
              {/* Header */}
              <View style={styles.header}>
                <View style={styles.timeDisplayRow}>
                  {/* Hour */}
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => {
                      setMode('hour')
                      setIsKeyboardMode(false)
                    }}
                    style={styles.timeDigitTouchable}
                  >
                    <Text
                      style={[
                        styles.timeDigitText,
                        {
                          opacity: mode === 'hour' && !isKeyboardMode ? 1.0 : 0.6
                        }
                      ]}
                    >
                      {isKeyboardMode ? kbHour : `${selectedHour}`}
                    </Text>
                  </TouchableOpacity>

                  <Text style={styles.colonText}>:</Text>

                  {/* Minute */}
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={() => {
                      setMode('minute')
                      setIsKeyboardMode(false)
                    }}
                    style={styles.timeDigitTouchable}
                  >
                    <Text
                      style={[
                        styles.timeDigitText,
                        {
                          opacity: mode === 'minute' && !isKeyboardMode ? 1.0 : 0.6
                        }
                      ]}
                    >
                      {isKeyboardMode
                        ? kbMinute
                        : selectedMinute < 10
                        ? `0${selectedMinute}`
                        : `${selectedMinute}`}
                    </Text>
                  </TouchableOpacity>

                  {/* AM / PM Selector */}
                  <View style={styles.ampmContainer}>
                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => setPeriod('AM')}
                      style={styles.ampmButton}
                    >
                      <Text
                        style={[
                          styles.ampmText,
                          { opacity: period === 'AM' ? 1.0 : 0.45 }
                        ]}
                      >
                        AM
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      activeOpacity={0.7}
                      onPress={() => setPeriod('PM')}
                      style={styles.ampmButton}
                    >
                      <Text
                        style={[
                          styles.ampmText,
                          { opacity: period === 'PM' ? 1.0 : 0.45 }
                        ]}
                      >
                        PM
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Body: Dial View or Keyboard Input */}
              {!isKeyboardMode ? (
                <View style={styles.dialContainer}>
                  <View style={styles.dialFace} {...panResponder.panHandlers}>
                    {/* Center Dot */}
                    <View style={styles.centerDot} />

                    {/* Hand Line */}
                    <View
                      style={[
                        styles.handLine,
                        {
                          left: midX - 1,
                          top: midY - NUMBER_RADIUS / 2,
                          height: NUMBER_RADIUS,
                          transform: [{ rotate: `${currentAngleDeg}deg` }]
                        }
                      ]}
                    />

                    {/* Selected Solid Teal Circle with Bold White Text */}
                    <View
                      style={[
                        styles.selectedIndicator,
                        {
                          left: targetX - ITEM_SIZE / 2,
                          top: targetY - ITEM_SIZE / 2
                        }
                      ]}
                      pointerEvents="none"
                    >
                      <Text style={styles.selectedIndicatorText}>
                        {mode === 'hour'
                          ? `${selectedHour}`
                          : selectedMinute < 10
                          ? `0${selectedMinute}`
                          : `${selectedMinute}`}
                      </Text>
                    </View>

                    {/* Dial Numbers */}
                    {mode === 'hour'
                      ? hoursList.map((h) => {
                          const deg = (h % 12) * 30
                          const rad = (deg * Math.PI) / 180
                          const nx = RADIUS + NUMBER_RADIUS * Math.sin(rad)
                          const ny = RADIUS - NUMBER_RADIUS * Math.cos(rad)
                          const isSelected = selectedHour === h

                          return (
                            <TouchableOpacity
                              key={`hour-${h}`}
                              activeOpacity={0.8}
                              onPress={() => selectHourDirect(h)}
                              style={[
                                styles.numberItem,
                                {
                                  left: nx - ITEM_SIZE / 2,
                                  top: ny - ITEM_SIZE / 2
                                }
                              ]}
                            >
                              {!isSelected && (
                                <Text style={styles.numberText}>{h}</Text>
                              )}
                            </TouchableOpacity>
                          )
                        })
                      : minutesList.map((m) => {
                          const deg = m * 6
                          const rad = (deg * Math.PI) / 180
                          const nx = RADIUS + NUMBER_RADIUS * Math.sin(rad)
                          const ny = RADIUS - NUMBER_RADIUS * Math.cos(rad)
                          const isSelected = selectedMinute === m

                          return (
                            <TouchableOpacity
                              key={`min-${m}`}
                              activeOpacity={0.8}
                              onPress={() => selectMinuteDirect(m)}
                              style={[
                                styles.numberItem,
                                {
                                  left: nx - ITEM_SIZE / 2,
                                  top: ny - ITEM_SIZE / 2
                                }
                              ]}
                            >
                              {!isSelected && (
                                <Text style={styles.numberText}>
                                  {m < 10 ? `0${m}` : `${m}`}
                                </Text>
                              )}
                            </TouchableOpacity>
                          )
                        })}
                  </View>
                </View>
              ) : (
                <View style={styles.keyboardContainer}>
                  <Text style={styles.kbPromptText}>Enter time</Text>
                  <View style={styles.kbInputRow}>
                    <View style={styles.kbInputGroup}>
                      <TextInput
                        style={styles.kbInput}
                        value={kbHour}
                        keyboardType="number-pad"
                        maxLength={2}
                        onChangeText={(val) => {
                          setKbHour(val)
                          const num = parseInt(val, 10)
                          if (!isNaN(num) && num >= 1 && num <= 12) {
                            setSelectedHour(num)
                          }
                        }}
                        selectTextOnFocus
                      />
                      <Text style={styles.kbLabel}>Hour</Text>
                    </View>

                    <Text style={styles.kbColon}>:</Text>

                    <View style={styles.kbInputGroup}>
                      <TextInput
                        style={styles.kbInput}
                        value={kbMinute}
                        keyboardType="number-pad"
                        maxLength={2}
                        onChangeText={(val) => {
                          setKbMinute(val)
                          const num = parseInt(val, 10)
                          if (!isNaN(num) && num >= 0 && num <= 59) {
                            setSelectedMinute(num)
                          }
                        }}
                        selectTextOnFocus
                      />
                      <Text style={styles.kbLabel}>Minute</Text>
                    </View>
                  </View>
                </View>
              )}

              {/* Footer */}
              <View style={styles.footer}>
                {/* Keyboard / Dial Switcher */}
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => setIsKeyboardMode(!isKeyboardMode)}
                  style={styles.modeIconButton}
                >
                  {isKeyboardMode ? (
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={24}
                      color="#666"
                    />
                  ) : (
                    <MaterialCommunityIcons
                      name="keyboard-outline"
                      size={24}
                      color="#666"
                    />
                  )}
                </TouchableOpacity>

                {/* Action Buttons */}
                <View style={styles.actionButtons}>
                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={onCancel}
                    style={styles.actionButton}
                  >
                    <Text style={styles.cancelText}>CANCEL</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={handleConfirm}
                    style={styles.actionButton}
                  >
                    <Text style={styles.okText}>OK</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modalLargeScreen: {
    maxWidth: '50%',
    right: 0,
    position: 'absolute',
    width: '100%',
    height: '100%'
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject
  },
  card: {
    width: 310,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    elevation: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8
  },
  header: {
    backgroundColor: PRIMARY_TEAL,
    paddingVertical: 18,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center'
  },
  timeDisplayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  timeDigitTouchable: {
    paddingHorizontal: 2,
    justifyContent: 'center',
    alignItems: 'center'
  },
  timeDigitText: {
    color: '#FFFFFF',
    fontSize: 54,
    fontWeight: '700',
    letterSpacing: 0.5
  },
  colonText: {
    color: '#FFFFFF',
    fontSize: 50,
    fontWeight: '700',
    marginHorizontal: 3,
    marginBottom: 4
  },
  ampmContainer: {
    marginLeft: 14,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  ampmButton: {
    paddingVertical: 1,
    paddingHorizontal: 3
  },
  ampmText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5
  },
  dialContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 22,
    backgroundColor: '#FFFFFF'
  },
  dialFace: {
    width: DIAL_SIZE,
    height: DIAL_SIZE,
    borderRadius: RADIUS,
    backgroundColor: '#ECEFF1',
    position: 'relative'
  },
  centerDot: {
    position: 'absolute',
    left: RADIUS - 4,
    top: RADIUS - 4,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: PRIMARY_TEAL,
    zIndex: 20
  },
  handLine: {
    position: 'absolute',
    width: 2,
    backgroundColor: PRIMARY_TEAL,
    zIndex: 10
  },
  selectedIndicator: {
    position: 'absolute',
    width: ITEM_SIZE,
    height: ITEM_SIZE,
    borderRadius: ITEM_SIZE / 2,
    backgroundColor: PRIMARY_TEAL,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 15
  },
  selectedIndicatorText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700'
  },
  numberItem: {
    position: 'absolute',
    width: ITEM_SIZE,
    height: ITEM_SIZE,
    borderRadius: ITEM_SIZE / 2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 12
  },
  numberText: {
    color: '#2C3E50',
    fontSize: 15,
    fontWeight: '600'
  },
  keyboardContainer: {
    paddingVertical: 32,
    paddingHorizontal: 24,
    alignItems: 'center'
  },
  kbPromptText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16
  },
  kbInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  kbInputGroup: {
    alignItems: 'center'
  },
  kbInput: {
    width: 80,
    height: 60,
    backgroundColor: '#ECEFF1',
    borderRadius: 8,
    textAlign: 'center',
    fontSize: 32,
    fontWeight: '700',
    color: PRIMARY_TEAL,
    borderWidth: 2,
    borderColor: PRIMARY_TEAL
  },
  kbLabel: {
    fontSize: 12,
    color: '#777',
    marginTop: 6
  },
  kbColon: {
    fontSize: 36,
    fontWeight: '700',
    color: '#333',
    marginHorizontal: 12,
    marginBottom: 20
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12
  },
  modeIconButton: {
    padding: 8
  },
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  actionButton: {
    paddingVertical: 8,
    paddingHorizontal: 14
  },
  cancelText: {
    color: CANCEL_RED,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.5
  },
  okText: {
    color: CONFIRM_GREEN,
    fontSize: 14,
    fontWeight: '700',
    letterSpacing: 0.5
  }
})

export default CustomTimePickerModal
